// ADR 15.10.26d §3 — four install reports, each through `deliver` (§0).
//
// `install.render`, the fifth, is `InstallBoundary.tsx`'s: it reads
// `promptAvailable()`, and this module must not import `pwa/installPrompt.ts`,
// which imports it. Every entry point here is total — a field report can never
// throw or reject into the code it observes.
//
// Every reporter takes its `DeviceInputs` (and a latch `storage`, and the
// hand-off watcher its `env`) as a parameter, defaulting to the globals, so the
// suites run under `node` on fakes.

import { deliver, latched, type FieldReport, type LatchStorage } from './delivery'
import {
  browserToken, caughtFingerprint, deviceRecord, deviceTags, readDeviceInputs, syntheticFingerprint,
  unknownProductToken, withHints, type DeviceInputs, type DeviceRecord,
} from './deviceRecord'

/** `telemetry/pwa.ts`'s `InstallSurface`, restated as `installStrip.ts` restates its types. */
type PromptSurface = 'strip' | 'header' | 'door' | 'account'

/** Build the record, wait for the hints, deliver what `build` makes of it — and swallow anything that throws. */
async function report(
  inputs: DeviceInputs,
  build: (r: DeviceRecord) => FieldReport,
  latchKey?: string,
  storage?: LatchStorage,
): Promise<void> {
  try {
    const record = await withHints(deviceRecord(inputs))
    deliver(build(record), latchKey, storage)
  } catch {
    // Total by contract (§0).
  }
}

/** A synthetic report's common shape: the device tags and record, plus the kind's own context. */
function synthetic(kind: string, r: DeviceRecord, extra: Record<string, Record<string, unknown>>): FieldReport {
  return {
    kind,
    transport: 'nonfatal',
    fingerprint: syntheticFingerprint(kind, r),
    tags: deviceTags(r),
    contexts: { device_record: r, ...extra },
  }
}

/**
 * `install.prompt_failed` — `prompt()` or `userChoice` rejected. The browser's
 * own error travels; `promptCaptured` is true because the caller held it.
 */
export function reportPromptFailed(error: unknown, surface: PromptSurface, inputs?: DeviceInputs): Promise<void> {
  const kind = 'install.prompt_failed'
  return report(inputs ?? readDeviceInputs(true), (r) => ({
    kind,
    transport: 'caught',
    error,
    fingerprint: caughtFingerprint(kind, r),
    tags: { ...deviceTags(r), 'install.surface': surface },
    contexts: { device_record: r, install: { surface } },
  }))
}

/** How long a tapped `intent://` link has to make the page react. */
export const HANDOFF_WAIT_MS = 10_000

type Listener = (event: Event) => void
interface ListenTarget {
  addEventListener(type: string, listener: Listener): void
  removeEventListener(type: string, listener: Listener): void
}

/** What `watchHandoff` observes, injectable so its suite runs on `EventTarget` fakes. */
export interface HandoffEnv {
  doc: ListenTarget & { visibilityState?: string }
  win: ListenTarget
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (id: unknown) => void
}

function globalEnv(): HandoffEnv | null {
  if (typeof document === 'undefined' || typeof window === 'undefined') return null
  return { doc: document, win: window }
}

/**
 * `install.handoff_ignored` — the Open link was tapped on the `android_chrome`
 * target and, 10 s later, the page has neither hidden, nor unloaded, nor lost
 * focus (a *Open in Chrome?* sheet takes focus while the page stays visible).
 * Every other target is ignored: `microsoft-edge:` raises its own dialog.
 */
export function watchHandoff(target: string, env?: HandoffEnv, inputs?: DeviceInputs): void {
  if (target !== 'android_chrome') return
  try {
    const resolved = env ?? globalEnv()
    if (resolved === null) return
    const { doc, win } = resolved
    const setTimer = resolved.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms))
    const clearTimer = resolved.clearTimer ?? ((id: unknown) => clearTimeout(id as ReturnType<typeof setTimeout>))
    const stop = () => {
      clearTimer(timer)
      doc.removeEventListener('visibilitychange', onVisibility)
      win.removeEventListener('pagehide', stop)
      win.removeEventListener('blur', stop)
    }
    const onVisibility = () => { if (doc.visibilityState === 'hidden') stop() }
    const timer = setTimer(() => {
      stop()
      void report(inputs ?? readDeviceInputs(false), (r) => synthetic('install.handoff_ignored', r, { handoff: { target } }))
    }, HANDOFF_WAIT_MS)
    doc.addEventListener('visibilitychange', onVisibility)
    win.addEventListener('pagehide', stop)
    win.addEventListener('blur', stop)
  } catch {
    // Total by contract (§0).
  }
}

/** The step keys the panel showed, and its fallback's when it had one. */
export interface StepsShown {
  steps: readonly string[]
  fallback?: readonly string[]
}

/** `install.steps_failed` — the learner said the steps did not work. */
export function reportStepsFailed(shown: StepsShown, inputs?: DeviceInputs): Promise<void> {
  const steps: Record<string, unknown> = { steps: [...shown.steps] }
  if (shown.fallback !== undefined) steps.fallback = [...shown.fallback]
  return report(inputs ?? readDeviceInputs(false), (r) => synthetic('install.steps_failed', r, { steps }))
}

let unrecognisedChecked = false

/** Test-only: the once-per-page-load flag. */
export function resetInstallReportsForTests(): void {
  unrecognisedChecked = false
}

/** The latch key both `unrecognised_browser` and its suite read. */
export function unrecognisedLatchKey(token: string, chromiumMajor: number): string {
  return `lingara.field.unrecognised.${token}.${chromiumMajor}`
}

/**
 * `install.unrecognised_browser` — once per page load, from `main.tsx`'s chain;
 * once per device per token and Chromium major. Deliberately not gated on
 * `platform === 'other'`: an unknown maker token on a modern engine reads as
 * `chrome_android` (15.10.26c Risk 6), and that is the case it exists for.
 */
export function reportUnrecognisedBrowser(inputs?: DeviceInputs, storage?: LatchStorage): Promise<void> {
  try {
    if (unrecognisedChecked) return Promise.resolve()
    unrecognisedChecked = true
    const resolved = inputs ?? readDeviceInputs(false)
    const token = unknownProductToken(resolved.ua)
    if (token === null) return Promise.resolve()
    const key = unrecognisedLatchKey(token, deviceRecord(resolved).chromiumMajor ?? 0)
    if (latched(key, storage)) return Promise.resolve()
    return report(resolved, (r) => synthetic('install.unrecognised_browser', r, { browser: { token: browserToken(r) } }), key, storage)
  } catch {
    return Promise.resolve()
  }
}
